import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import RefreshButton from "../../components/admin/RefreshButton";
import { useAlertModal } from "../../components/admin/AlertModal";
import Spinner from "../../components/ui/Spinner";
import { orgAdminService } from "../../features/org-admin/service";
import type { OrgCandidateUser, OrganizationSummary } from "../../features/org-admin/types";

const UserCell: React.FC<{ user: OrgCandidateUser }> = ({ user }) => (
  <td className="py-3 px-4">
    <div className="text-gray-200">{user.name || "Unnamed user"}</div>
    <div className="text-xs text-gray-500">{user.email}</div>
  </td>
);

const OrgAdminCandidatesPage: React.FC = () => {
  const [candidates, setCandidates] = useState<OrgCandidateUser[] | null>(null);
  const [invited, setInvited] = useState<OrgCandidateUser[] | null>(null);
  const [org, setOrg] = useState<OrganizationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const { alertModal, showAlert } = useAlertModal();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pool, pending, organization] = await Promise.all([
        orgAdminService.getCandidates(),
        orgAdminService.getInvited(),
        orgAdminService.getOrganization(),
      ]);
      setCandidates(pool);
      setInvited(pending);
      setOrg(organization);
    } catch (err: any) {
      console.error(err);
      showAlert(err?.response?.data?.detail || "Failed to load your candidate pool.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runAction = async (userId: string, action: "invite" | "uninvite") => {
    setBusyUserId(userId);
    try {
      if (action === "invite") {
        await orgAdminService.inviteCandidate(userId);
      } else {
        await orgAdminService.uninviteCandidate(userId);
      }
      await fetchAll();
    } catch (err: any) {
      console.error(err);
      showAlert(
        err?.response?.data?.detail ||
          (action === "invite" ? "Failed to send the invite." : "Failed to retract the invite."),
      );
    } finally {
      setBusyUserId(null);
    }
  };

  const seatsLeft = org ? Math.max((org.seat_limit || 0) - org.active_member_count, 0) : null;
  const seatsFull = seatsLeft === 0;

  return (
    <>
      <AdminPageHeader
        title="Candidates"
        subtitle="People Gullivr has matched to your company. Invite one and they'll see a join prompt inside their own account — there's no email link to chase."
        meta={
          org
            ? `${org.active_member_count}/${org.seat_limit} seats used`
            : undefined
        }
        actions={<RefreshButton onRefresh={fetchAll} />}
      />

      {seatsFull && (candidates?.length ?? 0) > 0 && (
        <div className="mb-6 p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400 text-sm">
          All {org?.seat_limit} seats are in use — remove a member or ask Gullivr for a larger plan before inviting anyone else.
        </div>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><Spinner /></div>
      ) : (
        <div className="space-y-6">
          <section>
            <h2 className="text-sm font-medium text-gray-300 mb-2">
              Awaiting acceptance ({invited?.length ?? 0})
            </h2>
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">User</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Account type</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Agency</th>
                      <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!invited || invited.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-10 text-center text-gray-500">
                          Nobody is waiting to accept right now.
                        </td>
                      </tr>
                    ) : (
                      invited.map((user) => (
                        <tr key={user.user_id} className="border-b border-gray-800/50">
                          <UserCell user={user} />
                          <td className="py-3 px-4 text-gray-400 text-xs">{user.role || "—"}</td>
                          <td className="py-3 px-4 text-gray-400 text-xs">{user.agency_name || "—"}</td>
                          <td className="py-3 px-4 text-right whitespace-nowrap">
                            <button
                              onClick={() => runAction(user.user_id, "uninvite")}
                              disabled={busyUserId === user.user_id}
                              className="px-2 py-1 text-xs text-amber-400 hover:bg-amber-500/10 rounded-md transition-colors disabled:opacity-50"
                            >
                              {busyUserId === user.user_id ? "Working…" : "Retract invite"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-sm font-medium text-gray-300 mb-2">
              Can be invited ({candidates?.length ?? 0})
            </h2>
            <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">User</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Account type</th>
                      <th className="text-left py-3 px-4 text-gray-500 font-medium">Agency</th>
                      <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!candidates || candidates.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-10 text-center text-gray-500">
                          Your candidate pool is empty. Gullivr curates who is eligible — contact support to have more of
                          your colleagues added.
                        </td>
                      </tr>
                    ) : (
                      candidates.map((user) => (
                        <tr key={user.user_id} className="border-b border-gray-800/50">
                          <UserCell user={user} />
                          <td className="py-3 px-4 text-gray-400 text-xs">{user.role || "—"}</td>
                          <td className="py-3 px-4 text-gray-400 text-xs">{user.agency_name || "—"}</td>
                          <td className="py-3 px-4 text-right whitespace-nowrap">
                            <button
                              onClick={() => runAction(user.user_id, "invite")}
                              disabled={busyUserId === user.user_id || seatsFull}
                              title={seatsFull ? "No seats left on your plan" : undefined}
                              className="px-2 py-1 text-xs text-indigo-400 hover:bg-indigo-500/10 rounded-md transition-colors disabled:opacity-50"
                            >
                              {busyUserId === user.user_id ? "Working…" : "Invite"}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
      )}
      {alertModal}
    </>
  );
};

export default OrgAdminCandidatesPage;
